import RdnaSmartTable, { RdnaSmartTableRef } from '@ringdna/common/src/components/RdnaTable/RdnaSmartTable'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Column } from 'react-table'
import { SFUser, useLicenceManagement, User, useUpdateLicenceManagement } from '../../../store/api/users-api'
import {
  COLUMN_WIDTH,
  getPaginationProps,
  License,
  NO_RESULT_STATE,
  PAGE_SIZE,
  RtnPagination,
  SERVER_PAGE_OFFSET,
  title,
  userSortDirection,
  userSortKeys
} from './utils'
import styled from 'styled-components'
import RdnaCheckbox from '@ringdna/common/src/components/RdnaFormElements/RdnaCheckbox'
import RdnaDrawer from '@ringdna/common/src/components/RdnaDrawer'
import { RdnaAccount } from '@ringdna/common/src/types/accounts'
import RdnaApiAutoComplete, {
  AutoCompleteApiData
} from '@ringdna/common/src/components/RdnaFormElements/RdnaApiAutoComplete'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import { serializeJsonIntoPlay } from '@ringdna/common/src/utils/serverUtils'
import { getErrorMessage } from '@ringdna/common/src/utils/errorUtils'
import { flags, isFlagged } from '@ringdna/common/src/utils/feature-flag/featureFlag'
import AvailableLicenses from './AvailableLicenses'

type Props = {
  isOpen: boolean
  onClose: () => void
  selectedAccount: RdnaAccount
  remainingLicenses: { [key: string]: number }
  onSave: (reload: boolean) => void
}

const HEAD = 'User License Management'
const SUB_HEAD = 'Assign licenses to users in your account. The number of available licenses are noted below.'

export default function LicenseManagement({ isOpen, onClose, selectedAccount, remainingLicenses, onSave }: Props) {
  const [userList, setUserList] = useState<SFUser[]>([])
  const [profile, setProfile] = useState<AutoCompleteApiData | undefined>()
  const [team, setTeam] = useState<AutoCompleteApiData | undefined>()
  const updateLicenceManagement = useUpdateLicenceManagement()
  const [isSaving, setIsSaving] = useState(false)

  const [availableLicenses, setAvailableLicenses] = useState<{ [key: string]: number }>({
    dialerLicense: remainingLicenses[License.INTELLIGENT_DIALER],
    caiLicense: remainingLicenses[License.CONVERSATION_AI],
    momentsLicense: remainingLicenses[License.MOMENTS],
    gsLicense: remainingLicenses[License.GUIDED_SELLING],
    revenueAILicense: remainingLicenses[License.REVENUE_AI],
    calendarLicense: remainingLicenses[License.CALENDAR],
    adminLicense: remainingLicenses[License.ADMIN]
  })

  const [queryState, setQueryState] = useState<RtnPagination>({
    globalFilter: '',
    sortBy: [{ id: 'displayName', desc: false }],
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })

  const [payload, , loading] = useLicenceManagement({
    query: {
      accountId: selectedAccount.id,
      globalFilter: queryState.globalFilter,
      teamId: team?.value ? parseInt(team.value) : null,
      profileId: profile?.value || null,
      'pager.pageSize': queryState.pageSize,
      'pager.currentPage': queryState.pageIndex + SERVER_PAGE_OFFSET,
      'pager.sortBy': userSortKeys(queryState.sortBy[0]?.id),
      'pager.sortDirection': userSortDirection(queryState.sortBy[0]?.desc)
    }
  })

  const usersTableRef = useRef<RdnaSmartTableRef | undefined>()
  const totalRowsRef = useRef(0)
  if (payload?.total) totalRowsRef.current = payload?.total

  const memoPayload = useMemo(() => {
    return payload?.results?.map((user: User) => ({
      ...user,
      userId: user.id,
      adminLicense: user.licenses.admin,
      dialerLicense: user.licenses.intelligentDialer,
      calendarLicense: user.licenses.calendar,
      gsLicense: user.licenses.guidedSelling,
      caiLicense: user.licenses.conversationAI,
      momentsLicense: user.licenses.moments,
      revenueAILicense: user.licenses.revenueAI
    }))
  }, [payload])

  const getValue = (user: any, license: string) => {
    const find: any = userList.find(u => u.userId === user.userId)
    return find ? find[license] : user[license]
  }

  const onChangeHeaderCheckbox = useCallback(
    (key: string, checked: boolean, users: any[]) => {
      let available = availableLicenses[key]
      const upd = users.map((user: any) => {
        const currentValue = getValue(user, key)
        if (checked && !currentValue) {
          available = available - 1
        } else if (!checked && currentValue) {
          available = available + 1
        }
        available >= 0 && setAvailableLicenses({ ...availableLicenses, [key]: available })
        return { ...user, [key]: available >= 0 ? checked : currentValue }
      })
      const list = userList
      upd.forEach(user => {
        const foundIndex = list.findIndex(u => u.userId === user.userId)
        if (foundIndex == -1) {
          list.push(user)
        } else {
          const find = list.find(u => u.userId === user.userId)
          // @ts-ignore
          list[foundIndex] = { ...find, [key]: user[key] }
        }
      })
      setUserList([...list.values()])
    },
    [availableLicenses, getValue, userList]
  )

  const onChangeCheckbox = useCallback(
    (key: string, checked: boolean, user: SFUser) => {
      const changed = userList.find(u => u.userId === user.userId)
      if (changed) {
        setUserList(userList.map(u => (u.userId === changed.userId ? { ...changed, [key]: checked } : u)))
      } else {
        setUserList([...userList.values(), { ...user, [key]: checked }])
      }
      setAvailableLicenses({ ...availableLicenses, [key]: availableLicenses[key] - (checked ? 1 : -1) })
    },
    [availableLicenses, setUserList, userList]
  )

  const handleQueryState = useCallback(
    (state: RtnPagination) => {
      setQueryState(state)
    },
    [setQueryState]
  )

  const getHeaderValue = (users: any[], license: string) => {
    if (users.length === 0) return false
    return (
      users.every(user => getValue(user, license)) ||
      (availableLicenses[license] <= 0 && users.some((user: any) => getValue(user, license)))
    )
  }

  const columns = useMemo<Column<SFUser>[]>(() => {
    const columns = [{ Header: 'Name', accessor: 'displayName', width: COLUMN_WIDTH } as Column<SFUser>]
    Object.keys(availableLicenses).forEach(license => {
      if (!isFlagged(flags.v299_REV_2816) && license === 'revenueAILicense') {
        return
      }
      columns.push({
        id: license,
        accessor: user => user,
        width: COLUMN_WIDTH,
        disableSortBy: true,
        Header: function headerCell(data) {
          return (
            <RdnaCheckbox
              className={'header-setting'}
              onChange={(checked: boolean) => onChangeHeaderCheckbox(license, checked, data.data as any[])}
              value={getHeaderValue(data.data as any[], license)}
              label={title(license)}
              labelPlacement={'top'}
            />
          )
        },
        Cell: (data: { value: SFUser }) => (
          <RdnaCheckbox
            className={'checkbox-setting'}
            onChange={(checked: boolean) => onChangeCheckbox(license, checked, data.value)}
            value={getValue(data.value, license)}
            disabled={!getValue(data.value, license) && availableLicenses[license] <= 0}
          />
        )
      } as Column<SFUser>)
    })
    columns.push(
      {
        id: 'profile',
        accessor: user => user,
        disableFilters: false,
        disableSortBy: true,
        width: 0,
        minWidth: 0,
        Cell: function createdCell() {
          return <></>
        },
        Filter: function fn() {
          return (
            <RdnaApiAutoComplete
              name="profile-filter"
              queryName={'profile'}
              placeholder={'Salesforce Profile'}
              onChange={event => {
                setProfile({ text: event.text, value: event.value })
                usersTableRef.current?.gotoPage(0)
              }}
              searchUrl={`/api/v2/app/users/salesforce/picklist`}
              defaultSearchTerm={profile?.text}
              onUpdateSearch={event => {
                if (!event.currentTarget.value) {
                  setProfile(undefined)
                  usersTableRef.current?.gotoPage(0)
                }
              }}
            />
          )
        }
      } as Column<SFUser>,
      {
        id: 'teams',
        accessor: user => user,
        disableFilters: false,
        disableSortBy: true,
        width: 0,
        minWidth: 0,
        Cell: function createdCell() {
          return <></>
        },
        Filter: function fn() {
          return (
            <RdnaApiAutoComplete
              name="teams-filter"
              placeholder={'Teams'}
              onChange={event => {
                setTeam({ text: event.text, value: event.value })
                usersTableRef.current?.gotoPage(0)
              }}
              searchQuery={{ accountId: selectedAccount.id }}
              searchUrl={'/api/v2/app/teams/picklist'}
              defaultSearchTerm={team?.text}
              onUpdateSearch={event => {
                if (!event.currentTarget.value) {
                  setTeam(undefined)
                  usersTableRef.current?.gotoPage(0)
                }
              }}
            />
          )
        }
      } as Column<SFUser>
    )
    return columns
  }, [
    availableLicenses,
    getHeaderValue,
    getValue,
    onChangeCheckbox,
    onChangeHeaderCheckbox,
    profile?.text,
    selectedAccount.id,
    team?.text
  ])

  const onSaveLicenses = () => {
    setIsSaving(true)
    updateLicenceManagement({
      body: serializeJsonIntoPlay({ users: [...userList], accountId: selectedAccount.id })
    })
      .catch(error => toastr.error(getErrorMessage(error)))
      .finally(() => {
        setIsSaving(false)
        onSave(true)
      })
  }

  return (
    <Drawer open={isOpen} onClose={onClose} heading={HEAD} subHeading={SUB_HEAD}>
      <Container>
        <AvailableLicenses availableLicenses={availableLicenses} />
        <RdnaSmartTable
          ref={usersTableRef}
          searchPlaceholder="Search"
          initialTableState={{
            globalFilter: '',
            sortBy: [{ id: 'displayName', desc: false }],
            pageIndex: 0,
            pageSize: PAGE_SIZE
          }}
          columns={columns}
          data={memoPayload || []}
          noResultState={NO_RESULT_STATE}
          setQueryState={handleQueryState}
          isFetching={!!loading}
          totalItems={totalRowsRef?.current}
          {...getPaginationProps(memoPayload, totalRowsRef?.current)}
        />
        <Footer>
          <RdnaButton text={'Cancel'} color="neutral" variant="text" onClick={onClose} />
          <RdnaButton
            text={'Save'}
            onClick={onSaveLicenses}
            disabled={!userList.length || isSaving}
            processing={isSaving}
          ></RdnaButton>
        </Footer>
      </Container>
    </Drawer>
  )
}

const Container = styled.div`
  [role='columnheader']:nth-child(n + 2) {
    height: auto;
    align-self: flex-end;
  }
  .header-setting label {
    span:last-child {
      text-align: center;
      white-space: normal;
      word-break: break-word;
    }
  }
  .checkbox-setting {
    align-content: center;
    flex-wrap: wrap;
    margin-left: ${({ theme }) => theme.spacing * 4.5}px;
  }
  .MuiTextField-root:has(input[name='global-search-filter']) {
    min-width: 100px;
  }
  .MuiTextField-root:has(input[id='teams-filter']) {
    padding-right: ${({ theme }) => theme.spacing * 2}px;
  }
`

const Drawer = styled(RdnaDrawer)`
  > div {
    width: 70%;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: end;
  padding-top: ${props => props.theme.spacing * 5}px;
`
